<script lang="ts">
  import { TRACK_NAMES, type DisplayView, type PublicPlayer, type Track } from '@banger/shared';
  import { betweenGoal } from '../lib/trackCopy';
  import Avatar from '../shared/Avatar.svelte';
  import PatternGrid from '../shared/PatternGrid.svelte';

  // Only Album has a between phase (after Tracks 1 and 2); `view.track` is the track just won.
  let { view, secs, byId }: { view: DisplayView; secs: number; byId: Map<string, PublicPlayer> } = $props();

  const next = $derived(Math.min(3, (view.track ?? 1) + 1) as Exclude<Track, 1>);
  const ranked = $derived((view.ranking ?? view.players.map((p) => p.id)).flatMap((id) => byId.get(id) ?? []));
  // 8 rows fit beside the next-track panel at 720p; the rest collapse into "+N more".
  const MAX_ROWS = 8;
  const rows = $derived(ranked.slice(0, MAX_ROWS));
  const more = $derived(ranked.length - rows.length);
  let innerHeight = $state(1080);

  const FULL = Array.from({ length: 25 }, (_, i) => i);
  // The Remix pattern is drawn as this screen opens, so the room sees it before Track 2.
  const pattern = $derived(next === 2 ? view.pattern : null);
</script>

<svelte:window bind:innerHeight />

<main>
  <section class="up">
    <p class="kicker">Next up</p>
    <h1 class="track" class:platinum={next === 3}>Track {next} · {TRACK_NAMES[next]}</h1>
    {#if next === 3}
      <PatternGrid platinum cells={FULL} label="the whole card" size="clamp(9rem, 26vh, 15rem)" />
    {:else if pattern}
      <div class="remix">
        <PatternGrid cells={pattern.cells} label={pattern.label} size="clamp(9rem, 26vh, 15rem)" />
        <p class="pname">{pattern.label}</p>
      </div>
    {/if}
    <p class="goal">{betweenGoal(next)}</p>
    <p class="next">Starting in <span class="neon">{secs}</span></p>
  </section>

  <section class="board">
    <h2 class="head">Scoreboard</h2>
    <table>
      <thead><tr><th></th><th></th><th>Pts</th><th>Correct</th></tr></thead>
      <tbody>
        {#each rows as p, i (p.id)}
          <tr style="--d:{i * 0.06}s" class:lead={i === 0} class:away={!p.connected}>
            <td class="rank">{i + 1}</td>
            <td class="who"><Avatar emoji={p.emoji} size={innerHeight < 900 ? 40 : 52} /><span class="name">{p.name}</span></td>
            <td class="num pts">{p.points ?? 0}</td>
            <td class="num">{p.correctTotal}</td>
          </tr>
        {/each}
      </tbody>
    </table>
    {#if more > 0}<p class="more">+{more} more</p>{/if}
  </section>
</main>

<style>
  main {
    min-height: 100%; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: center;
    /* Bottom padding keeps the scoreboard clear of the fixed host controls. */
    gap: 4vw; padding: 4vh 5vw 88px;
  }
  .up { display: grid; justify-items: center; gap: 2.2vh; text-align: center; }
  .kicker { margin: 0; font-size: clamp(1.2rem, 2.2vw, 2rem); font-weight: 800; letter-spacing: 0.3em; text-transform: uppercase; opacity: 0.8; }
  .track {
    margin: 0; font-size: clamp(2.6rem, 5.2vw, 5rem); font-weight: 900; line-height: 1.05;
    text-shadow: 0 0 10px var(--pink), 0 0 28px var(--pink), 0 0 60px var(--pink);
    animation: zoom 0.7s cubic-bezier(0.2, 1.6, 0.4, 1) both;
  }
  .track.platinum { text-shadow: 0 0 10px #fff, 0 0 26px #b8c4d6, 0 0 60px #7df9ff; }
  .goal { margin: 0; font-size: clamp(1.4rem, 2.5vw, 2.3rem); font-weight: 800; max-width: 24ch; line-height: 1.2; }
  .remix { display: grid; justify-items: center; gap: 1vh; }
  .pname { margin: 0; font-size: clamp(1.6rem, 3vw, 2.8rem); font-weight: 900; color: var(--cyan); text-shadow: 0 0 12px rgba(125, 249, 255, 0.7); }
  .next { margin: 0; font-size: clamp(1.3rem, 2.2vw, 2rem); font-weight: 700; opacity: 0.9; }
  .next .neon { font-weight: 900; font-variant-numeric: tabular-nums; }
  .board { display: grid; justify-items: center; gap: 1vh; }
  .head { margin: 0; font-size: clamp(2rem, 3.6vw, 3.4rem); font-weight: 900; color: var(--cyan); text-shadow: 0 0 14px rgba(125, 249, 255, 0.7); }
  table { border-collapse: separate; border-spacing: 0 0.7vh; font-size: clamp(1.2rem, 2.1vw, 2rem); }
  th { font-size: clamp(0.9rem, 1.3vw, 1.2rem); font-weight: 700; opacity: 0.65; text-transform: uppercase; letter-spacing: 0.1em; padding: 0 1.5vw; }
  tr { animation: in 0.45s var(--d) ease-out both; }
  tr.away { opacity: 0.55; }
  td { padding: 0.3vh 1.5vw; background: rgba(255, 255, 255, 0.06); }
  td:first-child { border-radius: 999px 0 0 999px; }
  td:last-child { border-radius: 0 999px 999px 0; }
  .lead td { background: rgba(255, 61, 242, 0.18); }
  .rank { font-weight: 900; opacity: 0.7; text-align: center; }
  .who { display: flex; align-items: center; gap: 12px; }
  .name { font-weight: 900; max-width: 11ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .num { text-align: center; font-weight: 900; font-variant-numeric: tabular-nums; }
  .pts { color: var(--pink); text-shadow: 0 0 10px var(--pink); }
  .more { margin: 0; font-size: clamp(1.1rem, 1.8vw, 1.7rem); font-weight: 800; opacity: 0.75; }
  @media (max-aspect-ratio: 1/1) { main { grid-template-columns: 1fr; } }
  @keyframes in { from { transform: translateY(20px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes zoom { from { transform: scale(0.4); opacity: 0; } to { transform: none; opacity: 1; } }
</style>
